/**
 * Loan Opening — Field Changes.
 *
 * Backend: eastview-os run-agent-reviews.md "Field-level diff + per-field revert".
 *   GET  /admin/loans/{dealId}/ai-reviews/field-writes            → the diff table
 *   POST .../field-writes/{provenanceId}/revert                   → undo one write
 *
 * The Loan Opening agent auto-populates empty loan fields; this shows the exact
 * per-field diff (field, old → new, source) it produced, with a per-row Revert.
 * Revert is GUARDED server-side — it returns 409 if the field was edited after
 * the agent wrote it, so a human change is never clobbered. Admin-only (the tab
 * that mounts it is admin-gated, matching the endpoints' require_admin).
 *
 * Rows whose `revertOf` is set are the reversal audit entries themselves, not
 * agent populations — filtered out so the table shows only what the agent wrote.
 *
 * UI: the card header is a collapse toggle whose sub-line previews the summary
 * ("32 fields populated · from 12 documents"). Expanded, a segmented switcher
 * offers three groupings of the same changes:
 *   • By document — accordion per source document (which doc wrote what).
 *   • By field    — accordion per entity key-path root (subjectProperties.0,
 *                   borrowers.1, entities.0, …); each row also names the doc.
 *   • List        — one flat list, each row naming its source document.
 * This is a grouped provenance display, not a flat multi-column records grid, so
 * the AG-Grid-for-tables convention doesn't apply — the value is in the grouping.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import HomeWorkOutlined from "@mui/icons-material/HomeWorkOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import RestoreRounded from "@mui/icons-material/RestoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ContactPageOutlined from "@mui/icons-material/ContactPageOutlined";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import colors, { ink, slate } from "../../../theme/colors";
import ExtractTrifoldModal from "../tabs/LoanDrilldownDocumentsTab/extracts/ExtractTrifoldModal";
import {
  useLoanExtractsIndex,
  findExtractForDoc,
} from "../tabs/LoanDrilldownDocumentsTab/extracts/useLoanExtractsIndex";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

// Source-cell palette, matched EXACTLY to the Opening Data Assistant (ODA) table's
// `T` tokens so the Modified Loan Data (MLD) Source Document cell reads identically:
// blue doc link, darker blue on hover, slate for non-links, grey for the type sub-line.
const SRC = {
  accent: "#2E4FD8",
  accentInk: "#22399B",
  ink2: "#4B5563",
  muted: "#8A919E",
};
// Truncation cap on the source link + type sub-line — same value as the ODA table.
const SOURCE_LINK_MAX_W = 165;

// --- Field-path + value formatting ----------------------------------------

const ACRONYMS = {
  apn: "APN",
  id: "ID",
  llc: "LLC",
  uw: "UW",
  hud: "HUD",
  sq: "Sq",
};
const CURRENCY_HINTS =
  /budget|insurance|taxes|cost|amount|price|premium|balance|payoff|reserve/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// Entity-array roots we split by index for the "By field" grouping + context.
const FIELD_ROOTS = {
  subjectProperties: "Property",
  entities: "Entity",
  borrowers: "Borrower",
  guarantors: "Guarantor",
};

const titleize = (segment) =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(" ")
    .map(
      (w) =>
        ACRONYMS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");

// "subjectProperties.0.address.apn" -> { label: "APN", context: "Property 1" }
const humanizeField = (path) => {
  const parts = String(path || "").split(".");
  let context = null;
  let rest = parts;
  if (FIELD_ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    context = `${FIELD_ROOTS[parts[0]]} ${Number(parts[1]) + 1}`;
    rest = parts.slice(2);
  }
  const leaf = rest[rest.length - 1] || path;
  return { label: titleize(leaf), context };
};

// "subjectProperties.0.address.apn" -> { key: "subjectProperties.0",
// label: "Property 1", root: "subjectProperties", index: 0 }. Top-level scalar
// fields (amortizationType, …) collapse into a single "Loan Details" group.
const fieldGroupOf = (path) => {
  const parts = String(path || "").split(".");
  if (FIELD_ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    return {
      key: `${parts[0]}.${parts[1]}`,
      label: `${FIELD_ROOTS[parts[0]]} ${Number(parts[1]) + 1}`,
      root: parts[0],
      index: Number(parts[1]),
    };
  }
  return { key: "loan", label: "Loan Details", root: "loan", index: null };
};

// The human name of the entity a field-group points at, read off the live loan
// (borrowers/guarantors → first+last, properties → address, entities → name).
// Returns null when there's no name to show (e.g. the "Loan Details" group).
const entityNameFor = (root, index, loan) => {
  if (index == null || !loan) return null;
  if (root === "subjectProperties") {
    return addressLabel(loan.subjectProperties?.[index]?.address) || null;
  }
  if (root === "entities") {
    return loan.entities?.[index]?.entityName || null;
  }
  if (root === "borrowers" || root === "guarantors") {
    const b = loan[root]?.[index];
    const name = `${b?.firstName || ""} ${b?.lastName || ""}`.trim();
    return name || null;
  }
  return null;
};

const GROUP_ICON = {
  subjectProperties: HomeWorkOutlined,
  entities: BusinessOutlined,
  borrowers: PersonOutlineRounded,
  guarantors: PersonOutlineRounded,
  loan: AccountBalanceOutlined,
};

// Render any extracted value (string / number / bool / empty / object / date).
const formatValue = (value, fieldPath = "") => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const formatted = value.toLocaleString("en-US");
    return CURRENCY_HINTS.test(fieldPath) ? `$${formatted}` : formatted;
  }
  if (typeof value === "object") {
    const label = addressLabel(value);
    if (label) return label;
    return Object.values(value).filter(Boolean).join(", ");
  }
  if (typeof value === "string" && ISO_DATE.test(value)) {
    // Date-only (midnight) → timezone-stable date; otherwise a real timestamp.
    const isMidnight =
      value.slice(11, 19) === "00:00:00" || value.slice(11, 16) === "00:00";
    return isMidnight
      ? formatDate(value, value)
      : formatDateTime(value) || value;
  }
  return String(value);
};

// --- Object / array value rendering (mirrors the Opening Data Assistant) ----

const isObjVal = (v) => v !== null && typeof v === "object";
const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Internal / noisy keys hidden from the diff table.
const HIDDEN_KEYS = new Set(["_id", "apnSource", "input_address", "displayAddress"]);

// One-line stand-in for an object/array value (address → fullAddress, member
// array → first member name + "+N more").
const summarizeObject = (v) => {
  if (Array.isArray(v)) {
    const first = v[0] || {};
    const name =
      first.fullName ||
      `${first.firstName || ""} ${first.lastName || ""}`.trim();
    return name
      ? `${name}${v.length > 1 ? ` +${v.length - 1} more` : ""}`
      : `${v.length} item${v.length === 1 ? "" : "s"}`;
  }
  if (v && typeof v === "object") {
    return (
      addressLabel(v) ||
      v.entityName ||
      Object.values(v).find((x) => typeof x === "string") ||
      "Object"
    );
  }
  return String(v);
};

// { text, empty?, obj? } for a Before / After cell.
const fmtDisplay = (v, path = "") => {
  if (v === null || v === undefined || v === "")
    return { text: "empty", empty: true };
  if (typeof v === "boolean") return { text: v ? "Yes" : "No" };
  if (typeof v === "number") {
    const f = v.toLocaleString("en-US");
    return { text: CURRENCY_HINTS.test(path) ? `$${f}` : f };
  }
  if (typeof v === "object") return { text: summarizeObject(v), obj: true };
  if (typeof v === "string" && ISO_DATE.test(v)) {
    const isMidnight =
      v.slice(11, 19) === "00:00:00" || v.slice(11, 16) === "00:00";
    return { text: isMidnight ? formatDate(v, v) : formatDateTime(v) || v };
  }
  return { text: String(v) };
};

// How many properties differ between old and new (for the "N changed" note).
const changedKeyCount = (cur, pro) => {
  const a = Array.isArray(pro) ? pro : [pro];
  const b = Array.isArray(cur) ? cur : [cur];
  let n = 0;
  a.forEach((item, i) => {
    const c = b[i] || {};
    const keys = new Set([...Object.keys(item || {}), ...Object.keys(c || {})]);
    keys.forEach((k) => {
      if (!HIDDEN_KEYS.has(k) && !eq(item?.[k], c?.[k])) n += 1;
    });
  });
  return n;
};

// A value as shown in a diff-table cell (nested objects/arrays → JSON).
const diffCell = (v) => {
  if (v === null || v === undefined || v === "")
    return (
      <Box component="span" sx={{ color: "#cbd5e1" }}>
        —
      </Box>
    );
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object")
    return (
      <Box
        component="span"
        sx={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 10.5,
          overflowWrap: "anywhere",
        }}
      >
        {JSON.stringify(v)}
      </Box>
    );
  return String(v);
};

// Rows for the Before vs After comparison of an object/array value. Arrays are
// split into "Item N" sections; nested objects/arrays render as JSON in a cell.
const buildDiffRows = (current, proposed) => {
  const rows = [];
  const pushObj = (c, p) => {
    const keys = [
      ...new Set([...Object.keys(c || {}), ...Object.keys(p || {})]),
    ].filter((k) => !HIDDEN_KEYS.has(k));
    keys.forEach((k) =>
      rows.push({ k, cur: c?.[k], pro: p?.[k], changed: !eq(c?.[k], p?.[k]) }),
    );
  };
  const arr = Array.isArray(proposed) || Array.isArray(current);
  if (arr) {
    const pArr = Array.isArray(proposed) ? proposed : proposed ? [proposed] : [];
    const cArr = Array.isArray(current) ? current : current ? [current] : [];
    const len = Math.max(pArr.length, cArr.length);
    for (let i = 0; i < len; i += 1) {
      rows.push({ section: `Item ${i + 1}` });
      pushObj(cArr[i] || {}, pArr[i] || {});
    }
  } else {
    pushObj(current || {}, proposed || {});
  }
  return rows;
};

// Before vs After detail table (Property · Before · After), changed rows tinted.
const ObjectDiffTable = ({ before, after }) => {
  const rows = buildDiffRows(before, after);
  const DIFF_COLS = "minmax(150px, 0.9fr) 1fr 1fr";
  return (
    <Box
      sx={{
        mt: "8px",
        border: "1px solid #e5e8ee",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "#fafbfc",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: DIFF_COLS,
          bgcolor: "#fff",
          borderBottom: "1px solid #e5e8ee",
        }}
      >
        {["Property", "Before", "After"].map((h) => (
          <Box
            key={h}
            sx={{
              px: "12px",
              py: "8px",
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            {h}
          </Box>
        ))}
      </Box>
      {rows.map((r, i) =>
        r.section ? (
          <Box
            key={`s${i}`}
            sx={{
              px: "12px",
              py: "6px",
              bgcolor: "#f1f5f9",
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
            }}
          >
            {r.section}
          </Box>
        ) : (
          <Box
            key={`${r.k}-${i}`}
            sx={{
              display: "grid",
              gridTemplateColumns: DIFF_COLS,
              borderTop: "1px solid #eef1f5",
              bgcolor: r.changed ? "#fffdf5" : "transparent",
            }}
          >
            <Box
              sx={{
                px: "12px",
                py: "7px",
                fontFamily: "ui-monospace, monospace",
                fontSize: 10.5,
                color: r.changed ? "#b45309" : "#64748b",
                fontWeight: r.changed ? 600 : 400,
                overflowWrap: "anywhere",
              }}
            >
              {r.k}
            </Box>
            <Box sx={{ px: "12px", py: "7px", fontSize: 11.5, color: "#94a3b8" }}>
              {diffCell(r.cur)}
            </Box>
            <Box
              sx={{
                px: "12px",
                py: "7px",
                fontSize: 11.5,
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              {diffCell(r.pro)}
            </Box>
          </Box>
        ),
      )}
    </Box>
  );
};

// Strip extension + underscores from filenames for display.
const prettyDocName = (name) =>
  String(name || "")
    .replace(/\.pdf$/i, "")
    .replace(/_/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

// Table look, mirrored EXACTLY from the Opening Data Assistant's `G` tokens so
// the Modified Loan Data sections/tables read identically: a section TITLE with a
// mono count chip sitting ABOVE a bordered, rounded, white table block whose
// header is a 34px small-caps rule and whose rows sit on hairlines with a 20px
// cell inset.
const G = {
  headerH: 34,
  pad: "20px",
  gap: "0 18px",
  line: "#e5e7eb", // block border + header rule
  lineSoft: "#eef2f6", // row rules
  faint: slate[400], // header labels
  ink,
  hover: slate[50],
  radius: "8px",
};

// The mono count pill beside a section title (ODA's `MetaChip`).
const MetaChip = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: "ui-monospace, monospace",
      fontSize: 10.5,
      fontWeight: 500,
      color: "#8A919E",
      bgcolor: G.hover,
      border: `1px solid ${G.line}`,
      borderRadius: "5px",
      px: 0.875,
      py: 0.125,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

// Count chip: "N fields" — same look as ODA's MetaChip.
const CountPill = ({ n }) => (
  <MetaChip>
    {n} {n === 1 ? "field" : "fields"}
  </MetaChip>
);

// Row column templates, shared by ChangeRow and the grouped-view column header so
// the header labels line up with the cells below. "doc" = the By-document view
// (no Source column — the document is the group header); "field" adds Source.
// The last column is a FIXED width (not `auto`): the rows' When+Revert cell is
// far wider than the header's lone "When" label, and an `auto` track would size
// each grid to its own content — shifting every column and misaligning the
// header from the rows. A fixed track keeps header and rows on the same columns.
// Field · Before · After · [Source] · When · Actions — Before/After, When and the
// Revert action are each their own left-aligned column (like the Opening Data
// Assistant's On loan / Proposed / Source split).
const WHEN_COL = "116px";
const ACTIONS_COL = "94px";
// Source is a FIXED track sized to its content (capped link) so it butts up
// against When with no dead space to its right — the flexible width goes to
// Before/After instead, which grow to fill the row.
const SOURCE_COL = "185px";
const DOC_ROW_TEMPLATE = `minmax(150px, 1fr) minmax(120px, 1fr) minmax(140px, 1.3fr) ${WHEN_COL} ${ACTIONS_COL}`;
const FIELD_ROW_TEMPLATE = `minmax(140px, 1fr) minmax(120px, 1.2fr) minmax(150px, 1.6fr) ${SOURCE_COL} ${WHEN_COL} ${ACTIONS_COL}`;

// --- Column sorting (click a header to sort every group's rows) -------------
// Which sort key each header label maps to. "Actions" has none (not sortable).
const SORT_KEY_BY_LABEL = {
  Field: "field",
  Before: "before",
  After: "after",
  "Source Document": "source",
  When: "when",
};

// Comparable value for a row under a sort key. Numbers stay numeric; everything
// else compares as a lowercased string (ISO dates sort chronologically as text).
const sortValueOf = (row, key) => {
  switch (key) {
    case "field":
      return (humanizeField(row.field).label || row.field || "").toLowerCase();
    case "before":
      return isObjVal(row.old) ? "" : (row.old ?? "");
    case "after":
      return isObjVal(row.new) ? "" : (row.new ?? "");
    case "source":
      return (row.sourceDocName || "").toLowerCase();
    case "when":
      return row.ts || "";
    default:
      return "";
  }
};

const compareBy = (a, b) => {
  const ae = a === "" || a == null;
  const be = b === "" || b == null;
  if (ae && be) return 0;
  if (ae) return 1; // empties sort last
  if (be) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// Sort a COPY of `rows` by the active {key, dir}; no-op when key is null.
const sortRowsBy = (rows, sort) => {
  if (!sort?.key) return rows;
  const out = [...rows].sort((r1, r2) =>
    compareBy(sortValueOf(r1, sort.key), sortValueOf(r2, sort.key)),
  );
  return sort.dir === "desc" ? out.reverse() : out;
};

// Column header for the grouped views (By document / By field). Rendered as the
// first row INSIDE each group card, so "Field / Before / After / Source Document
// / When" sit directly over their columns. Labels with a sort key are clickable
// (an arrow shows the active column + direction). Same grid as the rows.
const GroupColumnHeader = ({ showDoc, sort, onSort }) => {
  const labels = showDoc
    ? ["Field", "Before", "After", "Source Document", "When", "Actions"]
    : ["Field", "Before", "After", "When", "Actions"];
  const labelSx = {
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    textAlign: "left",
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: showDoc ? FIELD_ROW_TEMPLATE : DOC_ROW_TEMPLATE,
        gap: G.gap,
        alignItems: "center",
        px: G.pad,
        minHeight: G.headerH,
        bgcolor: "transparent",
        borderBottom: `1px solid ${G.line}`,
      }}
    >
      {labels.map((label) => {
        const key = SORT_KEY_BY_LABEL[label];
        if (!key || !onSort) {
          return (
            <Txt key={label} sx={{ ...labelSx, color: G.faint }}>
              {label}
            </Txt>
          );
        }
        const active = sort?.key === key;
        return (
          <Box
            key={label}
            component="button"
            type="button"
            onClick={() => onSort(key)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
              minWidth: 0,
              border: "none",
              bgcolor: "transparent",
              p: 0,
              cursor: "pointer",
              font: "inherit",
              "&:hover .mld-sort-lbl": { color: G.ink },
            }}
          >
            <Txt
              className="mld-sort-lbl"
              sx={{ ...labelSx, color: active ? G.ink : G.faint }}
            >
              {label}
            </Txt>
            <ExpandMoreRounded
              sx={{
                fontSize: 14,
                flexShrink: 0,
                color: active ? G.ink : "transparent",
                transition: "transform .15s",
                transform:
                  active && sort.dir === "asc" ? "rotate(180deg)" : "none",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// --- One field-change row --------------------------------------------------
// showDoc: give the source document its own column (used by the By-field and
// List views, where the document isn't already the group header).
// hideContext: suppress the "Property 1" chip when the group header already says it.

const ChangeRow = ({
  row,
  onRevert,
  onOpenDoc,
  showDoc = false,
  hideContext = false,
  gridTemplate = null,
}) => {
  const { label, context } = humanizeField(row.field);
  const oldDisp = fmtDisplay(row.old, row.field);
  const newDisp = fmtDisplay(row.new, row.field);
  const isObj = isObjVal(row.old) || isObjVal(row.new);
  const [objOpen, setObjOpen] = useState(false);
  const reverted = !!row.reverted;
  const canOpen = !!row.sourceDocOid && !!onOpenDoc;
  const changed = isObj ? changedKeyCount(row.old, row.new) : 0;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${G.lineSoft}`,
        "&:first-of-type": { borderTop: "none" },
        bgcolor: reverted ? "#fafbfc" : "transparent",
        "&:hover": reverted ? undefined : { bgcolor: G.hover },
      }}
    >
      <Box
        sx={{
          display: "grid",
          // `gridTemplate` (List view) overrides the responsive default so rows
          // line up with the resizable header.
          gridTemplateColumns:
            gridTemplate || (showDoc ? FIELD_ROW_TEMPLATE : DOC_ROW_TEMPLATE),
          alignItems: "center",
          gap: G.gap,
          px: G.pad,
          py: 1.25,
        }}
      >
        {/* Field */}
        <Box sx={{ minWidth: 0 }}>
          <Flx ac sx={{ gap: 0.75, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: reverted ? "#94a3b8" : "#1e293b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Txt>
            {!hideContext && context && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#64748b",
                  bgcolor: "#f1f5f9",
                  borderRadius: "4px",
                  px: 0.5,
                  py: "1px",
                }}
              >
                {context}
              </Box>
            )}
          </Flx>
          <Txt
            title={row.field}
            sx={{
              fontSize: 11,
              fontFamily: "ui-monospace, monospace",
              color: "#94a3b8",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.field}
          </Txt>
        </Box>

        {/* Before (prior value) */}
        <Box sx={{ minWidth: 0 }}>
          <Txt
            title={!oldDisp.obj ? oldDisp.text : undefined}
            sx={{
              fontSize: 12,
              fontStyle: oldDisp.empty ? "italic" : "normal",
              color: "#94a3b8",
              overflowWrap: "anywhere",
            }}
          >
            {oldDisp.text}
          </Txt>
        </Box>

        {/* After (written value) + object/array "Compare details" toggle */}
        <Box sx={{ minWidth: 0 }}>
          <Txt
            title={!newDisp.obj ? newDisp.text : undefined}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: reverted ? "#94a3b8" : "#0f172a",
              textDecoration: reverted ? "line-through" : "none",
              overflowWrap: "anywhere",
            }}
          >
            {newDisp.text}
          </Txt>
          {isObj && (
            <Flx ac wrap sx={{ gap: 1, mt: "3px" }}>
              <Txt sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                {oldDisp.empty
                  ? "new"
                  : `${changed} propert${changed === 1 ? "y" : "ies"} changed`}
              </Txt>
              <Box
                component="button"
                type="button"
                onClick={() => setObjOpen((v) => !v)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  font: "inherit",
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: colors.action,
                  bgcolor: "transparent",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {objOpen ? "Hide details" : "Compare details"}
                <ExpandMoreRounded
                  sx={{
                    fontSize: 13,
                    transition: "transform 0.15s ease",
                    transform: objOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </Box>
            </Flx>
          )}
        </Box>

        {/* Source Document — icon + name + type sub-line, matched EXACTLY to the
            ODA table's Source cell (blue link, OpenInFull glyph, grey sub-line). */}
        {showDoc && (
          <Box sx={{ minWidth: 0 }}>
            {row.sourceDocName ? (
              <>
                <Box
                  component={canOpen ? "button" : "span"}
                  type={canOpen ? "button" : undefined}
                  onClick={canOpen ? () => onOpenDoc(row) : undefined}
                  title={
                    canOpen ? `Open ${row.sourceDocName}` : row.sourceDocName
                  }
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: 0,
                    maxWidth: SOURCE_LINK_MAX_W,
                    font: "inherit",
                    fontSize: 11,
                    fontWeight: 600,
                    textAlign: "left",
                    color: canOpen ? SRC.accent : SRC.ink2,
                    bgcolor: "transparent",
                    border: "none",
                    p: 0,
                    cursor: canOpen ? "pointer" : "default",
                    "&:hover": canOpen ? { color: SRC.accentInk } : undefined,
                  }}
                >
                  <DescriptionOutlined sx={{ fontSize: 13, flexShrink: 0 }} />
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {prettyDocName(row.sourceDocName)}
                  </Box>
                </Box>
                {row.sourceDocType && (
                  <Txt
                    sx={{
                      display: "block",
                      fontSize: 10.5,
                      color: SRC.muted,
                      mt: "2px",
                      maxWidth: SOURCE_LINK_MAX_W,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.sourceDocType}
                  </Txt>
                )}
              </>
            ) : (
              <Txt sx={{ fontSize: 12, color: SRC.muted }}>—</Txt>
            )}
          </Box>
        )}

        {/* When — its own left-aligned column (rows are vertically centered). */}
        <Box sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 11, color: "#94a3b8", overflowWrap: "anywhere" }}>
            {formatDateTime(row.ts) || ""}
          </Txt>
        </Box>

        {/* Actions — Revert, its own left-aligned column */}
        <Box sx={{ minWidth: 0 }}>
          {reverted ? (
            <Chip
              size="small"
              label="Reverted"
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                height: 20,
                bgcolor: "#f1f5f9",
                color: "#64748b",
              }}
            />
          ) : (
            <Button
              size="small"
              onClick={() => onRevert(row)}
              startIcon={
                <RestoreRounded sx={{ fontSize: "14px !important", mr: -0.25 }} />
              }
              sx={{
                // Neutral bordered chip — same border / radius / height as the
                // ODA decision icon buttons; only the label text is red.
                textTransform: "none",
                minWidth: 0,
                height: 26,
                px: 1,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "7px",
                border: "1px solid #D5D9E0",
                bgcolor: "#fff",
                color: "#dc2626",
                "& .MuiButton-startIcon": { mr: 0.5 },
                "&:hover": {
                  color: "#b91c1c",
                  bgcolor: "#FAFBFC",
                  borderColor: "#c3ccd8",
                },
              }}
            >
              Revert
            </Button>
          )}
        </Box>
      </Box>

      {/* object/array value: full-width Before vs After detail table */}
      {isObj && objOpen && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <ObjectDiffTable before={row.old} after={row.new} />
        </Box>
      )}
    </Box>
  );
};

// --- Generic collapsible group shell (shared by doc + field groupings) ------

const CollapsibleGroup = ({
  icon: Icon,
  title,
  titleAttr,
  subtitle,
  meta,
  rightAction,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      {/* Section title on the card background (ODA TableSection anatomy): a
          rotating chevron, optional icon, title, mono count chip, and an
          optional right action — sitting ABOVE the bordered table block. */}
      <Flx
        ac
        g={1}
        sx={{
          py: 1.25,
          minWidth: 0,
          cursor: "pointer",
          userSelect: "none",
          "&:hover .mld-section-title": { color: G.ink },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronRightRounded
          sx={{
            fontSize: 18,
            color: G.faint,
            flexShrink: 0,
            transition: "transform .18s",
            transform: open ? "rotate(90deg)" : "none",
          }}
        />
        {Icon && (
          <Icon sx={{ fontSize: 15, color: "#8A919E", flexShrink: 0 }} />
        )}
        <Txt
          className="mld-section-title"
          title={titleAttr}
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: G.ink,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {title}
        </Txt>
        {subtitle && (
          <Txt
            title={subtitle}
            sx={{
              fontSize: 12.5,
              fontWeight: 500,
              color: "#64748b",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Txt>
        )}
        {meta}
        {rightAction ? (
          <Box sx={{ ml: "auto" }} onClick={(e) => e.stopPropagation()}>
            {rightAction}
          </Box>
        ) : null}
      </Flx>

      {open ? (
        <Box
          sx={{
            border: `1px solid ${G.line}`,
            borderRadius: G.radius,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

// --- Whole-object creations (borrower / entity / property / appraiser) ------
// The opening agent can CREATE missing objects, not just fill fields. Those
// arrive on the same feed with objectCreated:true — render as a "Created <kind>"
// card (not an old→new diff) with a Remove action (the same revert endpoint
// deletes the object). See run-agent-reviews.md "Whole-object creations".

const OBJECT_KIND = {
  borrower: { label: "Borrower", Icon: PersonOutlineRounded },
  entity: { label: "Entity", Icon: BusinessOutlined },
  subject_property: { label: "Property", Icon: HomeWorkOutlined },
  appraiser_contact: { label: "Appraiser Contact", Icon: ContactPageOutlined },
};

const ObjectCreatedCard = ({ row, first, onRemove, onOpenDoc }) => {
  const kind = OBJECT_KIND[row.objectKind] || {
    label: titleize(row.objectKind || "Object"),
    Icon: AddCircleOutlineRounded,
  };
  const KindIcon = kind.Icon;
  const removed = !!row.reverted;
  const summary = formatValue(row.new, row.field) || String(row.new || "—");
  const canOpen = !!row.sourceDocOid && !!onOpenDoc;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: first ? "none" : "1px solid #f1f5f9",
        bgcolor: removed ? "#fafbfc" : "transparent",
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          bgcolor: "#f5f3ff",
          color: "#7c3aed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <KindIcon sx={{ fontSize: 17 }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Flx ac sx={{ gap: 0.75, flexWrap: "wrap" }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: removed ? "#94a3b8" : "#0f172a",
            }}
          >
            Created {kind.label}
          </Txt>
          <Txt
            title={typeof summary === "string" ? summary : undefined}
            sx={{
              fontSize: 13,
              color: removed ? "#94a3b8" : "#334155",
              textDecoration: removed ? "line-through" : "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary}
          </Txt>
        </Flx>
        <Flx ac sx={{ gap: 0.75, mt: "2px", flexWrap: "wrap" }}>
          {row.sourceDocName && (
            <Box
              component="span"
              onClick={canOpen ? () => onOpenDoc(row) : undefined}
              title={row.sourceDocName}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: canOpen ? SRC.accent : SRC.muted,
                cursor: canOpen ? "pointer" : "default",
                "&:hover": canOpen ? { color: SRC.accentInk } : undefined,
              }}
            >
              {prettyDocName(row.sourceDocName) || row.sourceDocType}
            </Box>
          )}
          {row.sourceDocName && (
            <Box component="span" sx={{ color: "#cbd5e1" }}>
              ·
            </Box>
          )}
          <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
            {formatDateTime(row.ts) || ""}
          </Txt>
        </Flx>
      </Box>

      {removed ? (
        <Chip
          size="small"
          label="Removed"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            height: 20,
            bgcolor: "#f1f5f9",
            color: "#64748b",
            flexShrink: 0,
          }}
        />
      ) : (
        <Button
          size="small"
          onClick={() => onRemove(row)}
          startIcon={
            <DeleteOutlineRounded sx={{ fontSize: "15px !important", mr: -0.5 }} />
          }
          sx={{
            textTransform: "none",
            minWidth: 0,
            px: 1,
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            bgcolor: "transparent",
            "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
          }}
        >
          Remove
        </Button>
      )}
    </Box>
  );
};

// --- Panel -----------------------------------------------------------------

const VIEW_MODES = [
  { key: "field", label: "By section" },
  { key: "document", label: "By document" },
  { key: "list", label: "All changes" },
];

const OpeningFieldWritesPanel = ({ onCount, defaultExpanded = false }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const documents = useSelector((state) => state.loanDrilldown?.loanDocuments);
  const dealId = loan?._id;
  // Extract index for the trifold viewer — same source the ODA table uses.
  const { documents: extractDocs } = useLoanExtractsIndex(dealId);
  const [viewer, setViewer] = useState(null);

  const [rows, setRows] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [viewMode, setViewMode] = useState("field");
  const [search, setSearch] = useState("");
  // Per-table column sort — keyed by group id ("__all__" for the flat list) so
  // sorting one section's table doesn't reorder the others.
  const [sortByGroup, setSortByGroup] = useState({});
  const onSort = useCallback((groupKey, colKey) => {
    setSortByGroup((m) => {
      const cur = m[groupKey];
      const next =
        cur?.key === colKey
          ? { key: colKey, dir: cur.dir === "asc" ? "desc" : "asc" }
          : { key: colKey, dir: "asc" };
      return { ...m, [groupKey]: next };
    });
  }, []);
  const revertingRef = useRef(new Set()); // provenanceIds mid-revert (dedupe)

  const fetchRows = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const data = await losAiReviewsApi.getFieldWrites(dealId);
      // Only the agent populations — drop the reversal audit rows.
      setRows((Array.isArray(data) ? data : []).filter((r) => !r.revertOf));
    } catch {
      // Endpoint unreachable / nothing recorded — treat as empty.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleRevert = useCallback(
    async (row) => {
      const id = row?.provenanceId;
      if (!dealId || !id || revertingRef.current.has(id)) return;
      revertingRef.current.add(id);
      const kindLabel = (
        OBJECT_KIND[row.objectKind]?.label || "object"
      ).toLowerCase();
      try {
        await losAiReviewsApi.revertFieldWrite(dealId, id);
        enqueueSnackbar(
          row.objectCreated
            ? `Removed the created ${kindLabel}.`
            : `Reverted ${row.field} to its prior value.`,
          { variant: "success" },
        );
        fetchRows();
      } catch (err) {
        const status = err?.response?.status;
        const reason = err?.response?.data?.reason;
        // 409 = guarded: object already removed, or the field was edited after
        // the agent wrote it (no clobbering).
        enqueueSnackbar(
          status === 409
            ? reason ||
                (row.objectCreated
                  ? `Can't remove — this ${kindLabel} was already removed or changed.`
                  : "Can't revert — the field was changed after the agent wrote it.")
            : err?.response?.data?.detail ||
                err?.message ||
                (row.objectCreated
                  ? "Could not remove this object."
                  : "Could not revert this field."),
          { variant: status === 409 ? "warning" : "error" },
        );
      } finally {
        revertingRef.current.delete(id);
      }
    },
    [dealId, enqueueSnackbar, fetchRows],
  );

  // Open the field-write's source document — EXACTLY like the ODA table's Source
  // cell: resolve sourceDocOid to the loan document, and if that document has an
  // extract, open the trifold viewer focused on this field; otherwise fall back
  // to the docked file-preview sidebar.
  const handleOpenDoc = useCallback(
    (row) => {
      const oid = row?.sourceDocOid;
      if (!oid || !Array.isArray(documents)) return;
      const doc = documents.find(
        (d) => String(d?._id) === String(oid) || String(d?.oid) === String(oid),
      );
      const entry = findExtractForDoc(extractDocs, doc);
      if (entry?.parent_document_id) {
        setViewer({ selectedId: entry.parent_document_id, focusField: row.field });
        return;
      }
      if (doc) dispatch(filePreviewSetData(doc));
      else
        enqueueSnackbar("That source document isn't on this loan anymore.", {
          variant: "info",
        });
    },
    [documents, extractDocs, dispatch, enqueueSnackbar],
  );

  // Search across field path/label/context, both values, and the source doc.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => {
      const { label, context } = humanizeField(r.field);
      const hay = [
        r.field,
        label,
        context,
        formatValue(r.old, r.field),
        formatValue(r.new, r.field),
        r.sourceDocName,
        r.sourceDocType,
        r.objectKind,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  // Whole-object creations render as their own "Created" cards; the field-diff
  // views only deal with actual field writes.
  const objectRows = useMemo(
    () => filteredRows.filter((r) => r.objectCreated),
    [filteredRows],
  );
  const fieldRows = useMemo(
    () => filteredRows.filter((r) => !r.objectCreated),
    [filteredRows],
  );

  // Group by source document, preserving first-seen order.
  const byDocument = useMemo(() => {
    const map = new Map();
    for (const r of fieldRows) {
      if (!map.has(r.sourceDoc)) {
        map.set(r.sourceDoc, {
          doc: {
            id: r.sourceDoc,
            oid: r.sourceDocOid,
            name: r.sourceDocName,
            type: r.sourceDocType,
          },
          rows: [],
        });
      }
      map.get(r.sourceDoc).rows.push(r);
    }
    return [...map.values()];
  }, [fieldRows]);

  // Group by entity key-path root (subjectProperties.0, borrowers.1, …),
  // labelling each group with the entity's real name from the live loan.
  const byField = useMemo(() => {
    const map = new Map();
    for (const r of fieldRows) {
      const g = fieldGroupOf(r.field);
      if (!map.has(g.key)) {
        map.set(g.key, {
          ...g,
          name: entityNameFor(g.root, g.index, loan),
          rows: [],
        });
      }
      map.get(g.key).rows.push(r);
    }
    return [...map.values()];
  }, [fieldRows, loan]);

  const total = rows?.length || 0;
  const filteredTotal = filteredRows.length;
  const activeCount = useMemo(
    () => (rows || []).filter((r) => !r.reverted).length,
    [rows],
  );
  // Field populations vs whole-object creations (for the summary line).
  const fieldPopulatedCount = useMemo(
    () => (rows || []).filter((r) => !r.objectCreated && !r.reverted).length,
    [rows],
  );
  const revertedCount = total - activeCount;
  const docCount = byDocument.length;

  // Report the field-change count up to the section-tab badge (Modified Loan Data).
  useEffect(() => {
    onCount?.(total);
  }, [onCount, total]);

  // Hide the whole card when the Loan Opening agent has written nothing (or the
  // fetch hasn't landed yet) — no empty state, just don't render it.
  if (total === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderRadius: "14px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header — the collapse toggle. Sub-line previews the populated summary. */}
      <Flx
        ac
        onClick={() => setExpanded((v) => !v)}
        sx={{
          gap: 1.5,
          p: "18px 24px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #eef1f5" : "none",
          "&:hover": { bgcolor: "#fafbfc" },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "9px",
            background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
            border: "1px solid #e6e0fb",
            color: "#7c3aed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <EditNoteOutlined sx={{ fontSize: 24 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            sx={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "#0f172a",
              display: "block",
            }}
          >
            AI Changes from Documents
          </Txt>
          {/* Plain-language description of what this card is. */}
          <Txt
            sx={{
              fontSize: 12.5,
              color: "#64748b",
              lineHeight: 1.5,
              display: "block",
              maxWidth: 620,
            }}
          >
            The fields below were filled in automatically using information found
            while scanning your uploaded documents. Review each change, and undo
            anything that does not look right.
          </Txt>
        </Box>

        {/* Headline count — top-right, as a standout pill. */}
        <Box
          sx={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            bgcolor: "#eaeff5",
            border: "1px solid #d5dde8",
            borderRadius: "999px",
            px: 1.5,
            py: "5px",
          }}
        >
          <Txt sx={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>
            <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
              {fieldPopulatedCount}
            </Box>{" "}
            {fieldPopulatedCount === 1 ? "field" : "fields"}
            {docCount > 0 && (
              <>
                {" from "}
                <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {docCount}
                </Box>{" "}
                {docCount === 1 ? "document" : "documents"}
              </>
            )}
            {revertedCount > 0 && (
              <>
                {" · "}
                <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {revertedCount}
                </Box>{" "}
                reverted
              </>
            )}
          </Txt>
        </Box>

        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            fetchRows();
          }}
          disabled={loading}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: "#94a3b8",
            flexShrink: 0,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      {/* Body — a segmented switcher over three groupings of the same changes.
          White body (ODA anatomy): section titles sit on white, each table is its
          own bordered white block. */}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: "16px 24px 20px", bgcolor: "#fff" }}>
          {/* View switcher (#eef3f5 pill w/ #e3e9ec border per app convention).
              The By-field/document/List modes only apply to field rows, so the
              pill is hidden when the loan has only object creations. */}
          <Flx jb ac wrap sx={{ gap: 1, mb: 1.5 }}>
            {fieldRows.length > 0 ? (
            <Flx
              ac
              sx={{
                bgcolor: "#eef3f5",
                border: "1px solid #e3e9ec",
                borderRadius: "8px",
                p: "3px",
                gap: "2px",
              }}
            >
              {VIEW_MODES.map((m) => {
                const active = viewMode === m.key;
                return (
                  <Box
                    key={m.key}
                    component="button"
                    type="button"
                    onClick={() => setViewMode(m.key)}
                    sx={{
                      border: "none",
                      cursor: "pointer",
                      font: "inherit",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "6px",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      color: active ? "#0f172a" : "#64748b",
                      bgcolor: active ? "#fff" : "transparent",
                      boxShadow: active
                        ? "0 1px 2px rgba(15,23,42,0.08)"
                        : "none",
                      "&:hover": { color: active ? "#0f172a" : "#334155" },
                    }}
                  >
                    {m.label}
                  </Box>
                );
              })}
            </Flx>
            ) : (
              <Box />
            )}

            <TextField
              size="small"
              placeholder="Search fields, values, documents"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ fontSize: 17, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 12.5, bgcolor: "#fff" },
              }}
              sx={{ minWidth: 220, flex: "0 1 300px" }}
            />
          </Flx>

          {/* Whole-object creations — grouped above the field-diff rows. */}
          {objectRows.length > 0 && (
            <Box sx={{ mb: fieldRows.length > 0 ? 1.75 : 0 }}>
              <Txt
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  display: "block",
                  mb: 0.75,
                }}
              >
                Objects created ({objectRows.length})
              </Txt>
              <Box
                sx={{
                  border: "1px solid #e5e8ee",
                  borderRadius: "10px",
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                {objectRows.map((r, i) => (
                  <ObjectCreatedCard
                    key={r.provenanceId}
                    row={r}
                    first={i === 0}
                    onRemove={handleRevert}
                    onOpenDoc={handleOpenDoc}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* By document */}
          {viewMode === "document" && (
            <Flx column sx={{ gap: "18px" }}>
              {byDocument.map((g, i) => {
                const canOpen = !!g.doc.oid;
                const gSort = sortByGroup[g.doc.id];
                const gRows = sortRowsBy(g.rows, gSort);
                return (
                  <CollapsibleGroup
                    key={g.doc.id}
                    icon={DescriptionOutlined}
                    title={
                      prettyDocName(g.doc.name) ||
                      g.doc.type ||
                      "Source document"
                    }
                    titleAttr={g.doc.name}
                    defaultOpen={i < 2}
                    meta={
                      <Flx ac sx={{ gap: 0.5, flexShrink: 0 }}>
                        {g.doc.type && (
                          <Box
                            component="span"
                            sx={{
                              display: { xs: "none", sm: "inline" },
                              flexShrink: 0,
                              fontSize: 10,
                              fontWeight: 600,
                              color: "#94a3b8",
                              bgcolor: "#fff",
                              border: "1px solid #e5e8ee",
                              borderRadius: "4px",
                              px: 0.75,
                              py: "1px",
                            }}
                          >
                            {g.doc.type}
                          </Box>
                        )}
                        <CountPill n={g.rows.length} />
                      </Flx>
                    }
                    rightAction={
                      canOpen && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleOpenDoc(g.rows[0])}
                          endIcon={<OpenInNewRounded sx={{ fontSize: 13 }} />}
                          sx={{
                            textTransform: "none",
                            minWidth: 0,
                            flexShrink: 0,
                            fontSize: 11.5,
                            fontWeight: 600,
                          }}
                        >
                          View document
                        </Button>
                      )
                    }
                  >
                    <GroupColumnHeader
                      showDoc={false}
                      sort={gSort}
                      onSort={(col) => onSort(g.doc.id, col)}
                    />
                    {gRows.map((r) => (
                      <ChangeRow
                        key={r.provenanceId}
                        row={r}
                        onRevert={handleRevert}
                        onOpenDoc={handleOpenDoc}
                      />
                    ))}
                  </CollapsibleGroup>
                );
              })}
            </Flx>
          )}

          {/* By field (entity key-path root) */}
          {viewMode === "field" && (
            <Flx column sx={{ gap: "18px" }}>
              {byField.map((g, i) => {
                const gSort = sortByGroup[g.key];
                const gRows = sortRowsBy(g.rows, gSort);
                return (
                  <CollapsibleGroup
                    key={g.key}
                    icon={GROUP_ICON[g.root] || AccountBalanceOutlined}
                    title={g.label}
                    titleAttr={g.key}
                    subtitle={g.name}
                    defaultOpen={i < 2}
                    meta={<CountPill n={g.rows.length} />}
                  >
                    <GroupColumnHeader
                      showDoc
                      sort={gSort}
                      onSort={(col) => onSort(g.key, col)}
                    />
                    {gRows.map((r) => (
                      <ChangeRow
                        key={r.provenanceId}
                        row={r}
                        onRevert={handleRevert}
                        onOpenDoc={handleOpenDoc}
                        showDoc
                        hideContext
                      />
                    ))}
                  </CollapsibleGroup>
                );
              })}
            </Flx>
          )}

          {/* All changes — one flexible table block (ODA anatomy), columns fill
              the width like the grouped views. */}
          {viewMode === "list" && fieldRows.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${G.line}`,
                borderRadius: G.radius,
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              <GroupColumnHeader
                showDoc
                sort={sortByGroup.__all__}
                onSort={(col) => onSort("__all__", col)}
              />
              {sortRowsBy(fieldRows, sortByGroup.__all__).map((r) => (
                <ChangeRow
                  key={r.provenanceId}
                  row={r}
                  onRevert={handleRevert}
                  onOpenDoc={handleOpenDoc}
                  showDoc
                />
              ))}
            </Box>
          )}

          {/* No search matches */}
          {filteredTotal === 0 && (
            <Flx ac jc sx={{ py: 4 }}>
              <Txt sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                No field changes match “{search}”.
              </Txt>
            </Flx>
          )}
        </Box>
      </Collapse>

      {/* Extract trifold viewer — same modal the ODA table opens when a source
          document has extracts, focused on the field this row wrote. */}
      <ExtractTrifoldModal
        open={!!viewer}
        dealId={dealId}
        documents={extractDocs}
        selectedId={viewer?.selectedId}
        onSelectId={(id) => setViewer({ selectedId: id, focusField: null })}
        onClose={() => setViewer(null)}
        focusField={viewer?.focusField}
      />
    </Box>
  );
};

export default OpeningFieldWritesPanel;
